import { createContext, ReactNode, useContext, useEffect, useState } from 'react';

const COMPONENT_LOADED = 'tour-component-loaded-event';

type ComponentLoadedContextValue = {
	componentLoaded: boolean;
	componentId: string;
};

const ComponentLoadedContext = createContext<ComponentLoadedContextValue>({
	componentLoaded: false,
	componentId: '',
});

export const useComponentLoaded = () => {
	const ctx = useContext(ComponentLoadedContext);
	if (!ctx) throw new Error('useComponentLoaded must be used inside ComponentLoadedProvider');
	return ctx;
};

export const dispatchComponentLoaded = (id: string, loaded: boolean) => {
	window.dispatchEvent(
		new CustomEvent<{ loaded: boolean; id: string }>(COMPONENT_LOADED, { detail: { loaded, id } })
	);
};

export default function ComponentLoadedProvider({ children }: { children: ReactNode }) {
	const [componentLoaded, setComponentLoaded] = useState<boolean>(false);
	const [componentId, setComponentId] = useState<string>('');

	useEffect(() => {
		const handleComponentState = (event: CustomEvent<{ loaded: boolean; id: string }>) => {
			setComponentLoaded(event.detail.loaded);
			setComponentId(event.detail.id);
		};

		window.addEventListener(COMPONENT_LOADED, handleComponentState as EventListener);

		return () => {
			window.removeEventListener(COMPONENT_LOADED, handleComponentState as EventListener);
		};
	}, []);

	return (
		<ComponentLoadedContext.Provider
			value={{
				componentLoaded,
				componentId,
			}}
		>
			{children}
		</ComponentLoadedContext.Provider>
	);
}
